import { KatButton, KatModal } from '@amzn/katal-react';
import { FC, useEffect, useRef, useState } from 'react';
import styles from './ConfirmationDialog.module.css';

export interface AutoDismissConfirmationDialogProps {
  /**
   * Whether the dialog is visible
   */
  isVisible: boolean;
  /**
   * Title of the dialog
   */
  title: string;
  /**
   * Message to display in the dialog
   */
  message: string;
  /**
   * Text for the confirm button
   * @default "Confirm"
   */
  confirmText?: string;
  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;
  /**
   * Variant for the confirm button
   * @default "primary"
   */
  confirmVariant?: 'primary' | 'secondary' | 'danger';
  /**
   * Auto-dismiss timeout in milliseconds
   * @default 10000 (10 seconds)
   */
  autoConfirmTimeout?: number;
  /**
   * Callback when confirm is clicked or auto-confirmed
   */
  onConfirm: () => void;
  /**
   * Callback when cancel is clicked or dialog is closed
   */
  onCancel: () => void;
  /**
   * Additional class name for the dialog
   */
  className?: string;
  /**
   * Test ID for the dialog
   * @default "auto-dismiss-confirmation-dialog"
   */
  'data-testid'?: string;
}

/**
 * A confirmation dialog that auto-confirms after a specified timeout
 */
export const AutoDismissConfirmationDialog: FC<AutoDismissConfirmationDialogProps> = ({
  isVisible,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  autoConfirmTimeout = 10000,
  onConfirm,
  onCancel,
  className,
  'data-testid': testId = 'auto-dismiss-confirmation-dialog',
}) => {
  const [countdown, setCountdown] = useState(Math.ceil(autoConfirmTimeout / 1000));
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Ref to store the latest onConfirm callback to avoid restarting timers
   * when the callback changes. This prevents timer reset on every render.
   */
  const onConfirmRef = useRef(onConfirm);

  /**
   * Update the ref when onConfirm changes without restarting the timers.
   * This useEffect is necessary to keep the callback current while maintaining
   * stable timer references in the main useEffect.
   */
  useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);

  /**
   * Helper function to clear all active timers and prevent memory leaks
   */
  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isVisible) {
      setCountdown(Math.ceil(autoConfirmTimeout / 1000));

      // Start countdown interval
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          const newCount = Math.max(0, prev - 1);
          if (newCount === 0 && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return newCount;
        });
      }, 1000);

      // Set auto-confirm timeout - use ref to avoid timer reset on callback change
      timeoutRef.current = setTimeout(() => {
        onConfirmRef.current();
      }, autoConfirmTimeout);
    }

    return () => {
      clearTimers();
    };
  }, [isVisible, autoConfirmTimeout]);

  const handleCancel = () => {
    clearTimers();
    onCancel();
  };

  const handleConfirm = () => {
    clearTimers();
    onConfirm();
  };

  return (
    <KatModal
      visible={isVisible}
      onClose={handleCancel}
      className={`${styles.modal} ${className || ''}`}
      data-testid={testId}
    >
      <span slot="title">{title}</span>
      <div>
        <p className={styles.message}>{message}</p>
        <p className={styles.message}>
          Auto-confirming in {countdown} second{countdown !== 1 ? 's' : ''}...
        </p>
      </div>
      <div slot="footer" className={styles.footer}>
        <KatButton
          variant="secondary"
          label={cancelText}
          onClick={handleCancel}
          data-testid={`${testId}-cancel`}
        />
        <KatButton
          variant={confirmVariant}
          label={confirmText}
          onClick={handleConfirm}
          data-testid={`${testId}-confirm`}
        />
      </div>
    </KatModal>
  );
};
